import { useQuery } from '@tanstack/react-query';
import { getAppConfig } from '@/api';
import { hasSession, isTokenExchangeArrival } from '@/auth/session';
import { hasSelectedContext } from '@/auth/authData';
import { useAuthStore } from '@/store/useAuthStore';
import type { NewAppConfig } from '@/types';

/**
 * TanStack Query hook for the server-provided app config.
 *
 * App config is a small, slow-changing JSON payload (sort orders, status
 * definitions, validation rules, etc.) that the server returns once per
 * session. Owning it in TanStack Query rather than in `useAuthStore` lets
 * us reuse the cache's deduplication — concurrent ProtectedRoute mounts
 * coalesce into a single request — and removes the module-level
 * `configFetchInProgress` flag the audit doc flagged.
 *
 * `staleTime: Infinity` because the server doesn't push schema changes
 * within a session; if the user reloads the page we refetch fresh.
 * `enabled: hasSession() && hasContext`. The session half keeps us from
 * requesting before sign-in. The context half is not an optimization:
 * `GET /api/config/` is org/network-context scoped server-side (vingapi
 * `src/core/routers.py` — `Depends(get_access_context)`), so without a selected
 * context it can only answer 400 "No organization or network context selected".
 * The learner surface mounts this query (`QuestionDisplay` → `useUploadConfig`
 * on `/view/:stub`), where a context can never exist — that is vingreact#64 /
 * prod condition 28. Readers already degrade correctly without config (see
 * `uploadValidation.ts`: anonymous consume sessions never load it and the
 * mirrored fallbacks apply).
 */
export function useAppConfigQuery() {
  // Read from the store (not `useCurrentUser`) so the gate re-evaluates the
  // moment a context is selected and the query starts on its own.
  const hasContext = useAuthStore((s) => hasSelectedContext(s.user));

  return useQuery<NewAppConfig>({
    queryKey: ['appConfig'],
    queryFn: getAppConfig,
    // `!isTokenExchangeArrival()`: on `/sso?auth_token=…` the stored token
    // belongs to the ended session; firing config with it 401s and bounces the
    // page away before the arrival token is exchanged. Same gate as
    // useAuthQuery.
    enabled: hasSession() && !isTokenExchangeArrival() && hasContext,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
}
